import { bindAll } from 'bind-event-listener';

import { isSSR } from '../../../common/utils/is-ssr';

import { IDBOpenDBRequestNoOp } from './utils';

/**
 * Interface defining the wrapper-specific methods beyond IDBOpenDBRequest
 */
export interface IDBOpenDBRequestWrapperMethods {
	wrapRequest(request: IDBOpenDBRequest): void;
	isWrapping(): boolean;
	getWrappedRequest(): IDBOpenDBRequest | null;
	unbind(): void;
	setError(error: DOMException | null): void;
	setResult(result: IDBDatabase | null): void;
	setReadyState(state: IDBRequestReadyState): void;
	setSource(source: IDBObjectStore | IDBIndex | IDBCursor): void;
	setTransaction(transaction: IDBTransaction | null): void;
}

/**
 * Type combining IDBOpenDBRequest with wrapper methods
 */
export type IDBOpenDBRequestWrapperType = IDBOpenDBRequest & IDBOpenDBRequestWrapperMethods;

// Store the browser implementation class here - defined conditionally below
let BrowserImpl: (new () => IDBOpenDBRequestWrapperType) | undefined;

/**
 * Browser-only implementation that extends EventTarget.
 * Only defined in browser environments where EventTarget is available.
 * This conditional definition prevents "EventTarget is not defined" errors in SSR.
 */
if (typeof EventTarget !== 'undefined') {
	// Define the class inside this block so it's never parsed in SSR
	BrowserImpl = class IDBOpenDBRequestWrapperImpl extends EventTarget implements IDBOpenDBRequest {
	private _error: DOMException | null = null;
	private _result: IDBDatabase | null = null;
	private _readyState: IDBRequestReadyState = 'pending';
	private _source: IDBObjectStore | IDBIndex | IDBCursor = null as any;
	private _transaction: IDBTransaction | null = null;
	private _wrappedRequest: IDBOpenDBRequest | null = null;
	private _unbindEventListeners: (() => void) | null = null;

	// Implementing IDBRequest readonly properties with getters
	get error(): DOMException | null {
		return this._wrappedRequest?.error ?? this._error;
	}

	get result(): any {
		return this._wrappedRequest?.result ?? this._result;
	}

	get readyState(): IDBRequestReadyState {
		return this._wrappedRequest?.readyState ?? this._readyState;
	}

	get source(): IDBObjectStore | IDBIndex | IDBCursor {
		return this._wrappedRequest?.source ?? this._source;
	}

	get transaction(): IDBTransaction | null {
		return this._wrappedRequest?.transaction ?? this._transaction;
	}

	// Event handler properties (can be set directly by consumers)
	onerror: ((this: IDBRequest<any>, ev: Event) => any) | null = null;
	onsuccess: ((this: IDBRequest<any>, ev: Event) => any) | null = null;
	onblocked: ((this: IDBOpenDBRequest, ev: Event) => any) | null = null;
	onupgradeneeded: ((this: IDBOpenDBRequest, ev: IDBVersionChangeEvent) => any) | null = null;

	/**
	 * Wrap an existing IDBOpenDBRequest to proxy its events to this wrapper.
	 * Once wrapped, all property getters will delegate to the wrapped request,
	 * and all events from the wrapped request will be re-dispatched on this wrapper.
	 * 
	 * @param request - The real IDBOpenDBRequest to wrap
	 */
	wrapRequest(request: IDBOpenDBRequest): void {
		if (this._wrappedRequest) {
			throw new Error('This IDBOpenDBRequestWrapper is already wrapping another request');
		}

		this._wrappedRequest = request;

		// Proxy all events from the wrapped request to this wrapper using bindAll
		this._unbindEventListeners = bindAll(request, [
			{
				type: 'success',
				listener: (e: Event) => {
					this.dispatchEvent(new Event('success', e));
				},
			},
			{
				type: 'error',
				listener: (e: Event) => {
					this.dispatchEvent(new Event('error', e));
				},
			},
			{
				type: 'blocked',
				listener: (e: IDBVersionChangeEvent) => {
					const versionChangeEvent = new IDBVersionChangeEvent('blocked', e);
					this.dispatchEvent(versionChangeEvent);
				},
			},
			{
				type: 'upgradeneeded',
				listener: (e: IDBVersionChangeEvent) => {
					const versionChangeEvent = new IDBVersionChangeEvent('upgradeneeded', e);
					this.dispatchEvent(versionChangeEvent);
				},
			},
		]);
	}

	/**
	 * Check if this mock is wrapping a real request
	 */
	isWrapping(): boolean {
		return this._wrappedRequest !== null;
	}

	/**
	 * Get the wrapped request (if any)
	 */
	getWrappedRequest(): IDBOpenDBRequest | null {
		return this._wrappedRequest;
	}

	/**
	 * Unbind all event listeners attached to the wrapped request.
	 * Should be called when the wrapper is no longer needed.
	 */
	unbind(): void {
		if (this._unbindEventListeners) {
			this._unbindEventListeners();
			this._unbindEventListeners = null;
		}
		this._wrappedRequest = null;
	}

	/**
	 * Set the error property (normally readonly)
	 * Only works when not wrapping another request
	 */
	setError(error: DOMException | null): void {
		if (this._wrappedRequest) {
			throw new Error('Cannot set error on a wrapping IDBOpenDBRequestWrapper');
		}
		this._error = error;
	}

	/**
	 * Set the result property (normally readonly)
	 * Only works when not wrapping another request
	 */
	setResult(result: IDBDatabase | null): void {
		if (this._wrappedRequest) {
			throw new Error('Cannot set result on a wrapping IDBOpenDBRequestWrapper');
		}
		this._result = result;
	}

	/**
	 * Set the readyState property (normally readonly)
	 * Only works when not wrapping another request
	 */
	setReadyState(state: IDBRequestReadyState): void {
		if (this._wrappedRequest) {
			throw new Error('Cannot set readyState on a wrapping IDBOpenDBRequestWrapper');
		}
		this._readyState = state;
	}

	/**
	 * Set the source property (normally readonly)
	 * Only works when not wrapping another request
	 */
	setSource(source: IDBObjectStore | IDBIndex | IDBCursor): void {
		if (this._wrappedRequest) {
			throw new Error('Cannot set source on a wrapping IDBOpenDBRequestWrapper');
		}
		this._source = source;
	}

	/**
	 * Set the transaction property (normally readonly)
	 * Only works when not wrapping another request
	 */
	setTransaction(transaction: IDBTransaction | null): void {
		if (this._wrappedRequest) {
			throw new Error('Cannot set transaction on a wrapping IDBOpenDBRequestWrapper');
		}
		this._transaction = transaction;
	}

	/**
	 * Override dispatchEvent to also call the corresponding event handler if set
	 */
	override dispatchEvent(event: Event): boolean {
		const result = super.dispatchEvent(event);
		
		// Call the appropriate event handler if it exists
		switch (event.type) {
			case 'error':
				if (this.onerror) {
					this.onerror.call(this, event);
				}
				break;
			case 'success':
				if (this.onsuccess) {
					this.onsuccess.call(this, event);
				}
				break;
			case 'blocked':
				if (this.onblocked) {
					this.onblocked.call(this, event);
				}
				break;
			case 'upgradeneeded':
				if (this.onupgradeneeded) {
					this.onupgradeneeded.call(this, event as IDBVersionChangeEvent);
				}
				break;
		}
		
		return result;
	}
	} as new () => IDBOpenDBRequestWrapperType;
}

/**
 * Factory function to create an IDBOpenDBRequest wrapper.
 * In SSR environments, returns a minimal no-op implementation.
 * In browser environments, returns the full EventTarget-based implementation.
 */
export function createIDBOpenDBRequestWrapper(): IDBOpenDBRequestWrapperType {
	if (isSSR() || typeof EventTarget === 'undefined' || !BrowserImpl) {
		return new IDBOpenDBRequestNoOp() as IDBOpenDBRequestWrapperType;
	}
	return new BrowserImpl();
}

// Export for testing purposes - in browser environments (like jsdom), BrowserImpl will be defined
// In SSR, this will be undefined but that's ok since tests don't run in SSR
// We need to cast to 'any' to allow it to be used as both a type and a constructor in tests
export const IDBOpenDBRequestWrapper = BrowserImpl as any;
